'use client';

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Avatar,
} from '@ui/components';
import { Search, UserPlus, X } from 'lucide-react';
import { useLoader } from '@ui/contexts/loader-context';

export interface ISearchUser {
  _id: string;
  userName: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarURL: string;
}

interface UserSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUsers: (users: ISearchUser[]) => Promise<void>;
  existingParticipantIds?: string[];
  useLazySearchUsersQuery: any;
  title?: string;
  buttonText?: string;
  singleSelection?: boolean;
}

export function UserSearchModal({
  isOpen,
  onClose,
  onAddUsers,
  existingParticipantIds = [],
  useLazySearchUsersQuery,
  title = 'Add Users',
  buttonText = 'Add Users',
  singleSelection = false,
}: UserSearchModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<ISearchUser[]>([]);
  const [page, setPage] = useState(0);
  const { openLoader, closeLoader } = useLoader();

  const [searchUsers, { data, isLoading, isFetching }] = useLazySearchUsersQuery();

  // Debounce search
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedUsers([]);
      setPage(0);
      return;
    }

    const timer = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        searchUsers({ q: searchTerm.trim(), page: 0 });
        setPage(0);
      } else if (searchTerm.trim().length === 0) {
        // Clear results when search is empty
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, searchUsers]);

  // Load more results
  const handleLoadMore = () => {
    if (searchTerm.trim().length >= 2 && data && page < data.totalPages - 1) {
      const nextPage = page + 1;
      searchUsers({ q: searchTerm.trim(), page: nextPage });
      setPage(nextPage);
    }
  };

  // Filter out existing participants
  const availableUsers = useMemo(() => {
    if (!data?.users) return [];
    return data.users.filter(
      (user: ISearchUser) =>
        !existingParticipantIds.includes(user._id) &&
        !selectedUsers.some((s) => s._id === user._id),
    );
  }, [data?.users, existingParticipantIds, selectedUsers]);

  const handleToggleUser = (user: ISearchUser) => {
    if (singleSelection) {
      // For single selection, replace the selection
      setSelectedUsers([user]);
    } else {
      setSelectedUsers((prev) => {
        if (prev.some((u) => u._id === user._id)) {
          return prev.filter((u) => u._id !== user._id);
        }
        return [...prev, user];
      });
    }
  };

  const handleAddUsers = async () => {
    if (selectedUsers.length === 0) return;

    try {
      openLoader();
      await onAddUsers(selectedUsers);
      setSelectedUsers([]);
      setSearchTerm('');
      onClose();
    } catch (error) {
      // Error handling is done by parent component
    } finally {
      closeLoader();
    }
  };

  const hasMore = data && page < data.totalPages - 1;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full h-full max-w-full max-h-full rounded-none sm:w-auto sm:h-auto sm:max-w-2xl sm:max-h-[85vh] sm:rounded-lg p-0 gap-0 bg-white z-[9999] flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden px-6">
          <div className="space-y-4 flex-shrink-0 py-2">
            {/* Search Input */}
            <div className="relative px-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Selected Users */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                {selectedUsers.map((user) => (
                  <div
                    key={user._id}
                    className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-gray-300">
                    <Avatar
                      src={user.avatarURL}
                      displayName={user.displayName || user.userName}
                      size="xs"
                    />
                    <span className="text-sm text-gray-700">
                      {user.displayName || user.userName || user.firstName}
                    </span>
                    <button
                      onClick={() => handleToggleUser(user)}
                      className="text-gray-400 hover:text-gray-600">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Users List */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0 py-4">
            {searchTerm.trim().length < 2 ? (
              <div className="text-center py-8 text-gray-500">
                Type at least 2 characters to search for users
              </div>
            ) : isLoading && page === 0 ? (
              <div className="text-center py-8 text-gray-500">Searching users...</div>
            ) : availableUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm
                  ? 'No users found matching your search'
                  : 'Start typing to search for users'}
              </div>
            ) : (
              <>
                {availableUsers.map((user: ISearchUser) => {
                  const isSelected = selectedUsers.some((u) => u._id === user._id);
                  return (
                    <div
                      key={user._id}
                      onClick={() => handleToggleUser(user)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}>
                      <div className="flex items-center gap-3">
                        <Avatar
                          src={user.avatarURL}
                          displayName={user.displayName || user.userName}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">
                            {user.displayName ||
                              user.userName ||
                              `${user.firstName} ${user.lastName}`.trim()}
                          </div>
                          {user.userName && (
                            <div className="text-xs text-gray-500 mt-0.5">@{user.userName}</div>
                          )}
                        </div>
                        {isSelected && (
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                            <UserPlus className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {hasMore && (
                  <div className="pt-4 pb-2">
                    <Button
                      size="small"
                      type="secondary"
                      onClick={handleLoadMore}
                      disabled={isFetching}
                      className="w-full">
                      {isFetching ? 'Loading...' : 'Load More'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t flex-shrink-0 gap-2">
          <Button type="secondary" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={handleAddUsers}
            disabled={selectedUsers.length === 0}>
            {selectedUsers.length > 0
              ? singleSelection
                ? buttonText
                : `Add ${selectedUsers.length} User${selectedUsers.length > 1 ? 's' : ''}`
              : singleSelection
              ? 'Select a User'
              : 'Select Users to Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
